import { SDKParameters, Variation } from '@kameleoon/javascript-sdk';

/**
 * Service for managing Kameleoon feature flags and A/B testing.
 *
 * Provides a wrapper around the Kameleoon SDK for feature flag evaluation,
 * visitor identification, and account-level targeting.
 *
 * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags | Feature Flags Documentation}
 *
 * @example
 * ```typescript
 * const kameleoonService = pluginContext.serviceProvider.get<KameleoonService>(
 *   MASTERDATA_TOKENS.KameleoonService
 * );
 *
 * const isEnabled = await kameleoonService.isFeatureFlagActive('my-feature');
 * ```
 */
export interface KameleoonService {
  /**
   * The Kameleoon environment the client was initialized with (e.g. `integr`,
   * `qa`, `production`). `undefined` before {@link initialize} has run.
   *
   * Feature variables are not environment-scoped in Kameleoon, so consumers
   * that need environment-specific values can use this to select the right
   * entry from a variable that is keyed by environment.
   */
  readonly environment: string | undefined;

  /**
   * Whether the current visitor has granted consent for Kameleoon.
   *
   * Consent is determined by the hosting carrier (via its consent-management
   * platform) and pushed into the service through {@link setConsent} during app
   * bootstrap. When consent is absent the client is never initialized and all
   * Kameleoon reads fall back to their defaults, so consumers can use this to
   * decide whether to rely on Kameleoon-backed data or on local defaults.
   *
   * Defaults to `false` until {@link setConsent} has been called.
   *
   * @returns `true` when consent has been granted, `false` otherwise
   */
  hasConsent(): boolean;

  /**
   * Records whether the current visitor has granted consent for Kameleoon.
   *
   * @remarks
   * Called by the platform bootstrapper after resolving consent from the
   * carrier's consent-management platform, not by plugin developers.
   *
   * @param hasConsent - `true` when consent was granted, `false` otherwise
   */
  setConsent(hasConsent: boolean): void;

  /**
   * Initializes the Kameleoon client and associates the visitor with custom data.
   *
   * @remarks
   * This is typically called by the platform bootstrapper, not by plugin developers.
   * Custom Data keys: account ID = `0`, segment = `1`, country code = `2`.
   * Optional attributes are omitted from Custom Data when unavailable or empty.
   *
   * @param clientParameters - Configuration parameters for the Kameleoon SDK
   * @param accountId - The account ID to associate with the visitor (Custom Data key `0`)
   * @param countryCode - Optional raw channel `targetMarket` string (e.g. `DEU`, `AT`, `CH`);
   *   Kameleoon dashboard labels this field "country code". Omitted when unavailable.
   * @param segment - Optional account customer segment (`Global` | `High` | `Core` | `Self-Service`);
   *   omitted when unavailable
   * @returns Promise that resolves when initialization is complete
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#service-initialization | Feature Flags Documentation — Service Initialization}
   */
  initialize(
    clientParameters: SDKParameters,
    accountId: string,
    countryCode?: string,
    segment?: string
  ): Promise<void>;

  /**
   * Updates the visitor's country Custom Data (key `2`) with the channel target market.
   *
   * No-op if the client is not initialized or `countryCode` is empty.
   * Safe to call repeatedly (e.g. when the user switches channel).
   *
   * @param countryCode - Raw channel `targetMarket` string (e.g. `DEU`, `AT`, `CH`)
   */
  setCountryCode(countryCode: string): Promise<void>;

  /**
   * Checks if a feature flag is active for the current visitor.
   *
   * Returns `false` if initialization failed or the client is not available,
   * ensuring safe fallback behavior.
   *
   * @param featureName - The name of the feature flag (as configured in Kameleoon dashboard)
   * @returns Promise resolving to `true` if active, `false` otherwise
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#checking-feature-flags | Feature Flags Documentation — Checking Feature Flags}
   */
  isFeatureFlagActive(featureName: string): Promise<boolean>;

  /**
   * Returns the variation (experiment result) assigned to the current visitor
   * for the given feature flag.
   *
   * The returned {@link Variation} contains the variation `key`, `id`, the
   * associated `experimentId`, and a `Map` of the variation's variables, making
   * it the primary entry point for both experiment allocation and variable
   * reads.
   *
   * Returns `null` if initialization failed, the client is not available, or the
   * feature flag could not be evaluated, ensuring safe fallback behavior.
   *
   * @param featureKey - The feature flag key (as configured in the Kameleoon dashboard)
   * @param track - Whether to send a tracking request for the evaluation. Defaults to `true`
   * @returns Promise resolving to the assigned {@link Variation}, or `null` on error
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#resolving-a-variation | Feature Flags Documentation — Resolving a Variation}
   */
  getVariation(featureKey: string, track?: boolean): Promise<Variation | null>;

  /**
   * Returns the variations assigned to the current visitor across all feature
   * flags, keyed by feature flag key.
   *
   * Returns an empty `Map` if initialization failed or the client is not
   * available, ensuring safe fallback behavior.
   *
   * @param onlyActive - When `true`, only active (non-`off`) variations are returned. Defaults to `false`
   * @param track - Whether to send tracking requests for the evaluations. Defaults to `true`
   * @returns Promise resolving to a `Map` of feature flag keys to their {@link Variation}
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#resolving-all-variations | Feature Flags Documentation — Resolving All Variations}
   */
  getVariations(
    onlyActive?: boolean,
    track?: boolean
  ): Promise<Map<string, Variation>>;

  /**
   * Returns the value of a single variable of the variation assigned to the
   * current visitor for the given feature flag.
   *
   * Returns `defaultValue` if initialization failed, the client is not
   * available, the feature flag could not be evaluated, or the variable is not
   * present on the assigned variation, ensuring safe fallback behavior.
   *
   * @typeParam T - The expected type of the variable value
   * @param featureKey - The feature flag key (as configured in the Kameleoon dashboard)
   * @param variableKey - The variable key within the feature flag
   * @param defaultValue - The value to return when the variable cannot be resolved
   * @returns Promise resolving to the variable value, or `defaultValue` on error
   *
   * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-feature-flags#reading-a-single-variable | Feature Flags Documentation — Reading a Single Variable}
   */
  getFeatureVariable<T = unknown>(
    featureKey: string,
    variableKey: string,
    defaultValue?: T
  ): Promise<T | undefined>;
}
